import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ICreateNote, IGetNotesPayload, INote } from '../../types/notes';
import { logout, resetState } from '../GlobalAction';

export enum NotesSavingStatus {
  SAVING = 'SAVING',
  SAVED = 'SAVED',
  ERROR = 'ERROR',
}

export interface IProcessedAIResponse {
  title: string;
  data: {
    id: string;
    value: string;
  }[];
}

export interface INoteSuggestions {
  clientId: string;
  suggestions: IProcessedAIResponse[];
}

export interface IClientSpeechText {
  clientId: string;
  speechText: string;
}

export interface ISaveNoteRecordingPayload {
  clientId: string;
  coachId: string;
  noteId: string;
  recording: string;
  transcription: string;
  contentType: string;
  file: File;
  suggestions: { id: string; suggestion: string; type: string }[];
}

export interface IGetNoteRecordings {
  clientId: string;
  coachId: string;
  noteId: string;
}

export interface INoteRecording {
  id: string;
  transcription: string;
  recording: string;
  presignedUrl: string;
  noteId: string;
}

export interface IGetNoteSuggestionsInput {
  clientId: string;
  coachId: string;
  noteId: string;
}

export interface IGetNoteSuggestions {
  getNoteSuggestions: {
    id: string;
    clientId: string;
    coachId: string;
    noteId: string;
    recordingId: string;
    suggestion: string;
    type: string;
  }[];
}

interface INoteState {
  notes: INote[] | null;
  createdNote: ICreateNote | null;
  selectedNoteId: string | null;
  initialHtml: string | null;
  loading: boolean;
  error: string | null;
  presignedUrl: string | null;
  key: string | null;
  editNotePresignedUrl: string | null;
  savingStatus: NotesSavingStatus | null;
  notesSuggestions: INoteSuggestions[];
  clientSpeechText: IClientSpeechText[];
  deleteNoteLoading: boolean;
  deletedNoteId: string | null;
  deleteModalOpen: boolean;
  forceHtmlChange: number;
  currentNoteId: string;
  fetchNotesLoading: boolean;
  getPresignedUrlLoading: boolean;
  suggestion: string | null;
  clickedItems: string[];
  removedItems: string[];
  saveNoteRecordingLoading: boolean;
  getNoteRecordingsLoading: boolean;
  noteRecordings: INoteRecording[];
  deleteRecordingLabel: string;
  deleteNoteRecordingLoading: boolean;
  saveNoteSuggestionsLoading: boolean;
  saveNoteSuggestionsError: string | null;
  getNoteSuggestionsLoading: boolean;
  getNoteSuggestionsError: string | null;
  noteRecordingSuggestions: IGetNoteSuggestions | null;
  inputDeviceId: string | null;
}

const initialState: INoteState = {
  deletedNoteId: null,
  notes: null,
  initialHtml: null,
  selectedNoteId: null,
  loading: false,
  createdNote: null,
  error: null,
  presignedUrl: null,
  key: null,
  editNotePresignedUrl: null,
  savingStatus: null,
  notesSuggestions: [],
  clientSpeechText: [],
  forceHtmlChange: 0,
  deleteNoteLoading: false,
  deleteModalOpen: false,
  currentNoteId: '',
  fetchNotesLoading: false,
  getPresignedUrlLoading: false,
  suggestion: null,
  clickedItems: [],
  removedItems: [],
  saveNoteRecordingLoading: false,
  getNoteRecordingsLoading: false,
  noteRecordings: [],
  deleteRecordingLabel: '',
  deleteNoteRecordingLoading: false,
  saveNoteSuggestionsLoading: false,
  saveNoteSuggestionsError: null,
  getNoteSuggestionsLoading: false,
  getNoteSuggestionsError: null,
  noteRecordingSuggestions: null,
  inputDeviceId: null,
};

const noteSlice = createSlice({
  name: 'note',
  initialState,
  reducers: {
    fetchNotesRequest: (state: INoteState, _action: PayloadAction<IGetNotesPayload>) => {
      state.fetchNotesLoading = true;
    },
    fetchLastNoteRequest: (
      state: INoteState,
      _action: PayloadAction<{ clientId: string; coachId: string }>
    ) => {
      state.loading = true;
    },
    fetchLastNoteSuccess: (state: INoteState, action: PayloadAction<INote>) => {
      state.loading = false;
      state.notes = [...(state.notes || []), action.payload];
    },
    fetchLastNoteFailure: (state: INoteState, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
    fetchNotesSuccess: (state: INoteState, action: PayloadAction<INote[]>) => {
      state.notes = action.payload;
      state.fetchNotesLoading = false;
    },
    clearNotes: (state: INoteState) => {
      state.notes = null;
      state.selectedNoteId = null;
      state.initialHtml = null;
      state.forceHtmlChange = 0;
    },
    setSelectedNoteId: (state: INoteState, action: PayloadAction<string | null>) => {
      state.selectedNoteId = action.payload;
    },
    fetchNotesFailure: (state: INoteState, action: PayloadAction<string>) => {
      state.fetchNotesLoading = false;
      state.error = action.payload;
    },
    addTextNoteRequest: (
      state,
      action: PayloadAction<{
        tempId: string;
        clientId: string;
        coachId: string;
        text: string;
        html: string;
        transcription?: string;
        contentType?: string;
        file?: File;
        recording?: string;
      }>
    ) => {
      if (action.payload.transcription) {
        state.saveNoteRecordingLoading = true;
      } else {
        state.savingStatus = NotesSavingStatus.SAVING;
      }
    },
    clearNoteCache(state) {
      state.createdNote = null;
      state.presignedUrl = null;
      state.key = null;
      state.notesSuggestions = [];
      state.suggestion = null;
    },
    addNoteSuccess: (state, action: PayloadAction<ICreateNote>) => {
      if (
        state.notes &&
        state.notes.length > 0 &&
        state.notes[0].createdAt &&
        new Date(state.notes[0].createdAt).toISOString().split('T')[0] ===
          new Date(action.payload.createdAt ?? new Date()).toISOString().split('T')[0]
      ) {
        const counter = state.notes[0].counter + 1;
        const newNote = { ...action.payload, counter };
        state.notes = [newNote, ...state.notes];
      } else {
        const counter = 1;
        const newNote = { ...action.payload, counter };
        if (state.notes) {
          state.notes = [newNote, ...state.notes];
        } else {
          state.notes = [newNote];
        }
      }
      state.selectedNoteId = action.payload.id;
      state.createdNote = action.payload;
      state.savingStatus = NotesSavingStatus.SAVED;
      state.saveNoteRecordingLoading = false;
    },
    addNoteFailure: (state, action: PayloadAction<string>) => {
      state.savingStatus = NotesSavingStatus.ERROR;
      state.error = action.payload;
      state.saveNoteRecordingLoading = false;
    },
    getPresignedUrlRequest: (
      state,
      _action: PayloadAction<{
        clientId: string;
        fileName: string;
        contentType: string;
        fileObject: File | null;
      }>
    ) => {
      state.getPresignedUrlLoading = true;
    },
    getPresignedUrlSuccess: (
      state,
      action: PayloadAction<{ preSignedUrl: string; key: string }>
    ) => {
      state.getPresignedUrlLoading = false;
      state.presignedUrl = action.payload.preSignedUrl;
      state.key = action.payload.key;
    },
    getPresignedUrlFailure: (state, action: PayloadAction<string>) => {
      state.getPresignedUrlLoading = false;
      state.error = action.payload;
    },
    editSingleNoteRequest: (
      state,
      _action: PayloadAction<{
        clientId: string;
        coachId: string;
        noteId: string;
        text: string;
        isForceSuggestions: boolean;
        html: string;
      }>
    ) => {
      state.savingStatus = NotesSavingStatus.SAVING;
    },
    editNoteSuccess: (state, action: PayloadAction<INote>) => {
      if (state.notes) {
        state.notes = state.notes.map((note) => {
          if (note.id === action.payload.id) {
            const noteData = { ...action.payload };
            noteData.counter = note.counter;
            return noteData;
          }
          return note;
        });
      }
      state.savingStatus = NotesSavingStatus.SAVED;
    },
    saveCurrentNoteId: (state, action: PayloadAction<string>) => {
      state.currentNoteId = action.payload;
    },
    editNoteFailure: (state, action: PayloadAction<string>) => {
      state.savingStatus = NotesSavingStatus.ERROR;
      state.error = action.payload;
    },
    deleteSingleNoteRequest: (
      state,
      _action: PayloadAction<{
        id: string;
        clientId: string;
        coachId: string;
      }>
    ) => {
      state.deleteNoteLoading = true;
    },
    deleteNoteSuccess: (state, action: PayloadAction<string>) => {
      // find and remove the note from the notes array
      if (state.notes) {
        state.notes = state.notes?.filter((note) => note.id !== action.payload);
      }
      state.deleteNoteLoading = false;
      state.deletedNoteId = action.payload;
      state.deleteModalOpen = false;
    },
    deleteNoteFailure: (state, action: PayloadAction<string>) => {
      state.deleteNoteLoading = false;
      state.error = action.payload;
    },
    editMediaNoteRequest: (
      state,
      _action: PayloadAction<{
        noteId: string;
        clientId: string;
        coachId: string;
        fileName: string;
        type: string;
        contextType: string;
      }>
    ) => {
      state.loading = true;
    },

    editMediaNoteSuccess: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.editNotePresignedUrl = action.payload;
    },
    editMediaNoteFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
    resetSavingStatus: (state) => {
      state.savingStatus = null;
    },
    addClientSpeechText: (state, action: PayloadAction<IClientSpeechText>) => {
      // find the the client object with clientId and replace speechText
      // if already exists then replace else add
      const index = state.clientSpeechText.findIndex(
        (note) => note.clientId === action.payload.clientId
      );
      if (index !== undefined && index !== -1) {
        state.clientSpeechText[index] = {
          clientId: action.payload.clientId,
          speechText: state.clientSpeechText[index].speechText + action.payload.speechText,
        };
      } else {
        state.clientSpeechText = [...state.clientSpeechText, action.payload];
      }
    },
    setDeleteModalOpen: (state, action: PayloadAction<boolean>) => {
      state.deleteModalOpen = action.payload;
    },
    setSuggestion: (state, action: PayloadAction<string>) => {
      state.suggestion = action.payload;
    },
    setInitialHtml: (state, action: PayloadAction<string>) => {
      state.initialHtml = action.payload;
      state.forceHtmlChange += 1;
    },
    // Define appendClickItem action
    appendClickItem: (state, action: PayloadAction<string>) => {
      state.clickedItems.push(action.payload);
    },

    // Define removeClickItem action
    removeClickItem: (state, action: PayloadAction<string>) => {
      state.removedItems.push(action.payload);
    },

    // Define clearClickedAndRemovedItems action
    clearClickedAndRemovedItems: (state) => {
      state.clickedItems = [];
      state.removedItems = [];
    },
    saveNoteRecording: (state, _action: PayloadAction<ISaveNoteRecordingPayload>) => {
      state.saveNoteRecordingLoading = true;
    },
    saveNoteRecordingSuccess: (state, action: PayloadAction<INoteRecording>) => {
      state.saveNoteRecordingLoading = false;
      state.noteRecordings = [...state.noteRecordings, action.payload];
    },
    saveNoteRecordingFailure: (state) => {
      state.saveNoteRecordingLoading = false;
    },
    getNoteRecordings: (state: INoteState, _action: PayloadAction<IGetNoteRecordings>) => {
      state.getNoteRecordingsLoading = true;
    },
    getNoteRecordingsSuccess: (state: INoteState, action: PayloadAction<INoteRecording[]>) => {
      state.noteRecordings = action.payload;
      state.getNoteRecordingsLoading = false;
    },
    getNoteRecordingsFailure: (state: INoteState) => {
      state.getNoteRecordingsLoading = false;
    },
    setDeleteRecordingLabel: (state: INoteState, action: PayloadAction<string>) => {
      state.deleteRecordingLabel = action.payload;
    },
    deleteNoteRecordingRequest: (
      state: INoteState,
      _action: PayloadAction<{ clientId: string; coachId: string; recordingId: string }>
    ) => {
      state.deleteNoteRecordingLoading = true;
    },
    deleteNoteRecordingSuccess: (state: INoteState, action: PayloadAction<string>) => {
      state.deleteNoteRecordingLoading = false;
      state.noteRecordings = state.noteRecordings.filter(
        (recording) => recording.id !== action.payload
      );
    },
    deleteNoteRecordingFailure: (state: INoteState) => {
      state.deleteNoteRecordingLoading = false;
    },
    resetNoteRecordings: (state: INoteState) => {
      state.noteRecordings = [];
    },
    getNoteSuggestionsRequest: (state: INoteState, _action: PayloadAction<IGetNoteRecordings>) => {
      state.getNoteSuggestionsLoading = true;
    },
    getNoteSuggestionsSuccess: (state: INoteState, action: PayloadAction<IGetNoteSuggestions>) => {
      state.getNoteSuggestionsLoading = false;
      state.noteRecordingSuggestions = action.payload;
    },
    getNoteSuggestionsFailure: (state: INoteState, action: PayloadAction<string>) => {
      state.getNoteSuggestionsLoading = false;
      state.getNoteSuggestionsError = action.payload;
    },
    resetNoteSuggestions: (state: INoteState) => {
      state.noteRecordingSuggestions = null;
    },
    changeInputDevice: (state: INoteState, action: PayloadAction<string | null>) => {
      state.inputDeviceId = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(resetState, (state) => ({
      ...initialState,
      notesSuggestions: state.notesSuggestions,
      clientSpeechText: state.clientSpeechText,
    }));
    builder.addCase(logout, (state) => ({
      ...initialState,
      notesSuggestions: state.notesSuggestions,
      clientSpeechText: state.clientSpeechText,
    }));
  },
});

export const {
  fetchNotesRequest,
  fetchNotesSuccess,
  fetchNotesFailure,
  setInitialHtml,
  setSelectedNoteId,
  addTextNoteRequest,
  addNoteSuccess,
  clearNoteCache,
  addNoteFailure,
  getPresignedUrlRequest,
  getPresignedUrlSuccess,
  getPresignedUrlFailure,
  editSingleNoteRequest,
  editNoteSuccess,
  editNoteFailure,
  editMediaNoteRequest,
  editMediaNoteSuccess,
  editMediaNoteFailure,
  resetSavingStatus,
  addClientSpeechText,
  deleteSingleNoteRequest,
  deleteNoteSuccess,
  deleteNoteFailure,
  setDeleteModalOpen,
  saveCurrentNoteId,
  clearNotes,
  setSuggestion,
  appendClickItem,
  removeClickItem,
  clearClickedAndRemovedItems,
  saveNoteRecording,
  saveNoteRecordingSuccess,
  saveNoteRecordingFailure,
  getNoteRecordings,
  getNoteRecordingsSuccess,
  getNoteRecordingsFailure,
  setDeleteRecordingLabel,
  deleteNoteRecordingRequest,
  deleteNoteRecordingSuccess,
  deleteNoteRecordingFailure,
  resetNoteRecordings,
  getNoteSuggestionsRequest,
  getNoteSuggestionsSuccess,
  getNoteSuggestionsFailure,
  resetNoteSuggestions,
  changeInputDevice,
} = noteSlice.actions;

export default noteSlice.reducer;
